import { useEffect, useId, useRef, useState } from 'react';
import { parseDisplayedMeasurement } from '../engine/fieldNumber.js';
import { inputMeasurement } from '../engine/units.js';

/** Draft state for a direct-edit measurement in the Site's display unit. Only a
 * complete in-range number reaches onChange; other text stays visible with a
 * message. Key the caller by object identity so a draft never follows a new
 * selection. allowBlank passes null for an empty field (unknown, not quoted,
 * never); commit="blur" applies a valid value only when the field is left. */
export function useFieldMeasure({ doc, kind = 'length', value, onChange, min = -Infinity, max = Infinity, integer = false, allowBlank = false, commit = 'change', onProblem, unchanged = 'The plan was not changed.' }) {
  const shown = value == null || value === '' ? NaN : inputMeasurement(value, doc, kind), current = Number.isFinite(shown) ? String(shown) : '';
  const [draft, setDraft] = useState(null), focused = useRef(false), messageId = useId();
  useEffect(() => { if (!focused.current) setDraft(null); }, [current]);
  const read = text => allowBlank && !text.trim() ? { value: null } : parseDisplayedMeasurement(text, doc, kind, { min, max, integer });
  const problem = draft === null ? '' : read(draft).error || '';
  // Review callers apply their last accepted value, so they must know while a draft is unusable.
  const report = useRef(onProblem); report.current = onProblem;
  useEffect(() => { report.current?.(!!problem); return () => report.current?.(false); }, [!!problem]);
  const apply = text => { const result = read(text); if (!result.error && (commit !== 'blur' || result.value !== (value ?? null))) onChange(result.value); };
  return {
    problem, messageId, unchanged,
    input: {
      type: 'text', inputMode: 'decimal', autoComplete: 'off', spellCheck: false, value: draft ?? current,
      'aria-invalid': problem ? 'true' : undefined, 'aria-describedby': problem ? messageId : undefined,
      onFocus: () => { focused.current = true; },
      onBlur: () => { focused.current = false; if (commit === 'blur' && draft !== null) apply(draft); if (!problem) setDraft(null); },
      onKeyDown: commit === 'blur' ? e => { if (e.key === 'Enter') e.currentTarget.blur(); } : undefined,
      onChange: e => { const text = e.target.value; setDraft(text); if (commit !== 'blur') apply(text); },
    },
  };
}

export const FieldMeasureMessage = ({ field }) => field.problem
  ? <p id={field.messageId} className="formerror" role="alert">{field.problem} {field.unchanged}</p> : null;

/** A strict number input for custom markup (an existing label/id or table cell).
 * Extra props (id, aria-label, placeholder, disabled…) pass to the input. */
export function StrictNumberInput({ doc, kind = 'number', value, onChange, min, max, integer, allowBlank, commit, onProblem, unchanged = 'The previous value was kept.', ...props }) {
  const field = useFieldMeasure({ doc, kind, value, onChange, min, max, integer, allowBlank, commit, onProblem, unchanged });
  const describedBy = [props['aria-describedby'], field.input['aria-describedby']].filter(Boolean).join(' ') || undefined;
  return <><input {...props} {...field.input} aria-describedby={describedBy} /><FieldMeasureMessage field={field} /></>;
}
